import { useState } from 'react'
import styled from 'styled-components'
import Link from 'next/link'
import SwiperCore, { Pagination } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { breakpoint } from '../layout/styles'
import { Container } from '../styles/Container'
import Cta from '../styles/Cta'
import { Grid, Cell } from '../styles/Grid'
import { useMediaQuery } from '../helpers/mq'

SwiperCore.use([Pagination])

const InspirationGrid = ({ data }: any) => {
  const desktop = useMediaQuery(breakpoint.desktop)
  const [more, setMore] = useState<boolean>(false)
  const items = !more ? data.items.slice(0, data.show_main_tile ? 5 : 6) : data.items

  const toggleMore = () => {
    setMore(!more)
  }

  const GridRender = (
    <Content>
      <GridContainer>
        {data.show_main_tile && (
          <MainSection>
            {data.main_tile.title && <h3>{data.main_tile.title}</h3>}
            {data.main_tile.description && <p>{data.main_tile.description}</p>}
            {data.main_tile.cta?.href && (
              <Link href={data.main_tile.cta.href}>
                <Cta>{data.main_tile.cta.title}</Cta>
              </Link>
            )}
          </MainSection>
        )}
        {items.map((item: any) => (
          <Section key={item._metadata.uid}>
            {item.icon && <img src={item.icon?.url} />}
            {item.name && <h4>{item.name}</h4>}
            {item.tagline && <em>{item.tagline}</em>}
            {item.description && <p>{item.description}</p>}
            {(item.call_to_action?.title || item.call_to_action?.link?.href) && (
              <p>
                {item.call_to_action.title && (
                  <>
                    <b>{item.call_to_action.title}</b>
                    <br />
                  </>
                )}
                {item.call_to_action.link && (
                  <Link href={item.call_to_action.link.href}>
                    <a>{item.call_to_action.link.title}</a>
                  </Link>
                )}
              </p>
            )}
          </Section>
        ))}
      </GridContainer>
      {data.show_main_tile && (
        <Cta light={true} onClick={toggleMore}>
          Show {more ? 'Less' : 'More'}
        </Cta>
      )}
    </Content>
  )

  const SlideRender = (
    <Content>
      <Swiper
        pagination={{ clickable: true }}
        slidesPerView={'auto'}
        spaceBetween={24}
        centeredSlides={true}
        breakpoints={{
          768: {
            spaceBetween: 40,
            slidesPerGroup: 1,
            centeredSlides: false
          }
        }}>
        {data.show_main_tile && (
          <SwiperSlide>
            <MainSection>
              {data.main_tile.title && <h3>{data.main_tile.title}</h3>}
              {data.main_tile.description && <p>{data.main_tile.description}</p>}
              {data.main_tile.cta?.href && (
                <Link href={data.main_tile.cta.href}>
                  <Cta>{data.main_tile.cta.title}</Cta>
                </Link>
              )}
            </MainSection>
          </SwiperSlide>
        )}
        {items.map((item: any) => (
          <SwiperSlide key={item._metadata.uid}>
            <Section>
              {item.icon && <img src={item.icon?.url} />}
              {item.name && <h4>{item.name}</h4>}
              {item.tagline && <em>{item.tagline}</em>}
              {item.description && <p>{item.description}</p>}
              {(item.call_to_action?.title || item.call_to_action?.link?.href) && (
                <p>
                  {item.call_to_action.title && (
                    <>
                      <b>{item.call_to_action.title}</b>
                      <br />
                    </>
                  )}
                  {item.call_to_action.link && (
                    <Link href={item.call_to_action.link.href}>
                      <a>{item.call_to_action.link.title}</a>
                    </Link>
                  )}
                </p>
              )}
            </Section>
          </SwiperSlide>
        ))}
      </Swiper>
    </Content>
  )

  return <Wrapper>{desktop ? GridRender : SlideRender}</Wrapper>
}

export default InspirationGrid

const GridContainer = styled(Grid)`
  flex-wrap: wrap;
`

const Content = styled(Container)`
  margin: 0 auto;
  padding: 58px 23px;
  @media ${breakpoint.desktop} {
    padding: 108px 0;
    max-width: 1120px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  ${Cta} {
    background: white;
    color: black;
    border: 1.25px solid #000000;
    padding: 10px 20px;
    border-radius: 2.5px;
    font-size: 16px;
    flex: 0 0 auto;
  }

  .swiper-container {
    padding-bottom: 30px;
  }

  .swiper-slide {
    height: auto;
    padding-bottom: 30px;
  }

  .swiper-pagination {

  }
`

export const Wrapper = styled(Container)`
  color: var(--text);
  background: #f5e09f;
  max-width: 100%;
  padding: 0.1px;
  margin: 0;
`

export const Section = styled(Cell)`
  text-align: center;
  width: 100%;
  margin: 20px 0;
  padding: 64px 32px;
  height: 100%;
  background: #fff;
  @media ${breakpoint.desktop} {
    height: inherit;
    margin: 20px 16px;
    max-width: calc(33.3333% - 21.33px);
    &:nth-child(3n + 1) {
      margin-left: 0;
    }
    &:nth-child(3n + 3) {
      margin-right: 0;
    }
  }
  p {
    font-size: 20px;
    line-height: 26px;
    margin: 36px 0 0;
    a,
    a:visited {
      color: black;
    }
  }
  h4 {
    font-size: 20px;
    line-height: 1.3;
    margin: 5px 0;
  }
  em {
    font-size: 12px;
    line-height: 1.3;
    font-style: normal;
  }
  img {
    width: 100%;
    height: auto;
    max-width: 132px;
    max-height: 132px;
    object-fit: contain;
    margin-bottom: 15px;
    display: inline-block;
  }
`

const MainSection = styled(Section)`
  background: #000;
  color: #fff;
  text-align: left;
  h3 {
    font-weight: 400;
    font-size: 36px;
    line-height: 1.1;
    text-transform: uppercase;
    margin: 0 0 40px;
  }
  p {
    margin: 20px 0;
    font-weight: 325;
    font-size: 16px;
    line-height: 1.3;
  }
  ${Cta} {
    color: black;
    background: white;
  }
`
